import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, Send, Loader2, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MessagesPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const targetUserId = searchParams.get('user');
  const jobId = searchParams.get('job');

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (targetUserId) {
      setSelectedUser({ id: targetUserId });
      fetchMessages(targetUserId);
    }
  }, [targetUserId]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages`);
      const msgs = response.data;
      
      // Group by conversation partner
      const convMap = new Map();
      msgs.forEach(msg => {
        const partnerId = msg.sender_id === user.id ? msg.recipient_id : msg.sender_id;
        const partnerName = msg.sender_id === user.id ? msg.recipient_name : msg.sender_name;
        
        if (!convMap.has(partnerId)) {
          convMap.set(partnerId, {
            id: partnerId,
            name: partnerName,
            lastMessage: msg.content,
            lastMessageTime: msg.created_at,
            unread: msg.recipient_id === user.id && !msg.read
          });
        }
      });
      
      setConversations(Array.from(convMap.values()));
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${userId}`);
      setMessages(response.data);
      scrollToBottom();
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedUser.id,
        job_id: jobId || null,
        content: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedUser.id);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedUser(conv);
    fetchMessages(conv.id);
  };

  const getDashboardLink = () => {
    return user?.role === 'contractor' ? '/contractor/dashboard' : '/customer/dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950" data-testid="messages-page">
      {/* Header */}
      <header className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_clearing-crew/artifacts/qizuc7gv_file_00000000d05471f59cc81c7b9868657c.png" 
                alt="IronLink" 
                className="h-14"
              />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to={getDashboardLink()} 
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </Link>

        <h1 className="font-heading text-3xl text-white uppercase mb-6">Messages</h1>

        <div className="grid lg:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <div className="industrial-card overflow-hidden">
            <div className="p-4 border-b border-stone-700">
              <h2 className="font-heading text-lg text-white uppercase">Conversations</h2>
            </div>
            <ScrollArea className="h-[calc(600px-60px)]">
              {conversations.length === 0 ? (
                <div className="p-4 text-center text-stone-400">
                  No conversations yet
                </div>
              ) : (
                <div className="divide-y divide-stone-700">
                  {conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className={`w-full p-4 text-left hover:bg-stone-800 transition-colors ${
                        selectedUser?.id === conv.id ? 'bg-stone-800' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-stone-700 flex items-center justify-center">
                          <User className="w-5 h-5 text-stone-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{conv.name}</p>
                          <p className="text-stone-400 text-sm truncate">{conv.lastMessage}</p>
                        </div>
                        {conv.unread && (
                          <span className="w-2 h-2 rounded-full bg-orange-500" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Messages Area */}
          <div className="lg:col-span-2 industrial-card flex flex-col overflow-hidden">
            {selectedUser ? (
              <>
                <div className="p-4 border-b border-stone-700">
                  <h2 className="font-heading text-lg text-white">{selectedUser.name || 'New Conversation'}</h2>
                </div>
                
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-sm p-3 ${
                            msg.sender_id === user.id
                              ? 'bg-orange-500 text-white'
                              : 'bg-stone-800 text-stone-200'
                          }`}
                        >
                          <p>{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender_id === user.id ? 'text-orange-200' : 'text-stone-500'
                          }`}>
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                <form onSubmit={handleSend} className="p-4 border-t border-stone-700">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-stone-900 border-stone-700 h-12"
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit"
                      disabled={sending || !newMessage.trim()}
                      data-testid="send-message-btn"
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6"
                    >
                      {sending ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-stone-400">
                Select a conversation or start a new one
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
